import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Button } from '@goaltree/ui/components/button'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useQuery } from 'convex/react'
import { Camera, ImagePlus, LoaderCircle, RotateCcw, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CameraSetting } from 'simli-client'

type SimliSceneCall = {
  setFaceCamera: (camera: CameraSetting | null, lerpSeconds?: number) => boolean
  setFaceBackground: (backgroundId: string | null) => boolean
  resetCamera: () => void
  closerCamera: () => void
}

async function prepareBackground(file: File): Promise<Blob> {
  const image = await createImageBitmap(file)
  try {
    const targetRatio = 9 / 16
    const sourceRatio = image.width / image.height
    const cropWidth = sourceRatio > targetRatio ? image.height * targetRatio : image.width
    const cropHeight = sourceRatio > targetRatio ? image.height : image.width / targetRatio
    const canvas = document.createElement('canvas')
    canvas.width = 720
    canvas.height = 1280
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Could not prepare image')
    context.drawImage(
      image,
      (image.width - cropWidth) / 2,
      (image.height - cropHeight) / 2,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    )
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.78),
    )
    if (!blob) throw new Error('Could not prepare image')
    if (blob.size > 900_000) throw new Error('Image is too large after resizing')
    return blob
  } finally {
    image.close()
  }
}

export function CoachScenePicker({ call }: { call: SimliSceneCall }) {
  const { t } = useTranslation()
  const assets = useQuery(api.voiceCall.listMySimliBackgrounds, {})
  const upload = useAction(api.voiceCall.uploadMySimliBackground)
  const remove = useAction(api.voiceCall.deleteMySimliBackground)
  const fileRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function chooseFile(file: File | undefined) {
    if (!file) return
    setError(null)
    setBusy(true)
    try {
      const image = await prepareBackground(file)
      const asset = await upload({
        image: await image.arrayBuffer(),
        name: file.name.replace(/\.[^.]+$/, '').slice(0, 70) || 'My background',
      })
      if (!call.setFaceBackground(asset.simliId)) throw new Error(t('callView.sceneNotLive'))
      setSelected(asset._id)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('callView.sceneUploadFailed'))
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function deleteAsset(id: Id<'simliBackgrounds'>) {
    setError(null)
    setBusy(true)
    try {
      await remove({ id })
      if (selected === id) {
        call.setFaceBackground(null)
        setSelected(null)
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('callView.sceneUploadFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative pointer-events-auto">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('callView.scene')}
        aria-expanded={open}
        title={t('callView.scene')}
        className="size-9 rounded-full bg-black/45 text-white backdrop-blur-md hover:bg-black/65 hover:text-white"
        onClick={() => setOpen((value) => !value)}
      >
        <Camera className="size-4" />
      </Button>
      {open && (
        <div
          className="absolute right-0 top-11 z-40 w-[min(19rem,calc(100vw-2rem))] rounded-2xl border border-white/15 bg-black/80 p-3 text-white shadow-2xl backdrop-blur-xl"
          onPointerDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
        >
          <p className="text-xs font-bold">{t('callView.camera')}</p>
          <p className="mt-1 text-[11px] leading-relaxed text-white/65">{t('callView.dragHint')}</p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={call.resetCamera}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-white/10 px-2 py-2 text-[11px] font-semibold hover:bg-white/15"
            >
              <RotateCcw className="size-3.5" /> {t('callView.resetView')}
            </button>
            <button
              type="button"
              onClick={call.closerCamera}
              className="flex-1 rounded-lg bg-white/10 px-2 py-2 text-[11px] font-semibold hover:bg-white/15"
            >
              {t('callView.closer')}
            </button>
          </div>

          <div className="my-3 h-px bg-white/10" />
          <p className="text-xs font-bold">{t('callView.background')}</p>
          <div className="mt-2 flex flex-col gap-1.5">
            <button
              type="button"
              aria-pressed={!selected}
              onClick={() => {
                call.setFaceBackground(null)
                setSelected(null)
              }}
              className={cn(
                'flex items-center justify-between rounded-lg px-2.5 py-2 text-left text-[11px] hover:bg-white/10',
                !selected && 'bg-white/10',
              )}
            >
              {t('callView.originalBackground')}
              {!selected && <span className="text-[#7ed957]">✓</span>}
            </button>
            {(assets ?? []).map((asset) => (
              <div key={asset._id} className="flex items-center gap-1 rounded-lg hover:bg-white/10">
                <button
                  type="button"
                  aria-pressed={selected === asset._id}
                  onClick={() => {
                    if (call.setFaceBackground(asset.simliId)) setSelected(asset._id)
                    else setError(t('callView.sceneNotLive'))
                  }}
                  className="flex min-w-0 flex-1 items-center justify-between px-2.5 py-2 text-left text-[11px]"
                >
                  <span className="truncate">{asset.name}</span>
                  {selected === asset._id && <span className="ml-2 text-[#7ed957]">✓</span>}
                </button>
                <button
                  type="button"
                  aria-label={t('callView.deleteBackground', { name: asset.name })}
                  disabled={busy}
                  onClick={() => void deleteAsset(asset._id)}
                  className="mr-1 flex size-7 shrink-0 items-center justify-center rounded-md text-white/55 hover:bg-white/10 hover:text-white disabled:opacity-40"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            ))}
            {assets?.length === 0 && (
              <p className="px-2.5 py-1 text-[10px] text-white/50">{t('callView.noBackgrounds')}</p>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(event) => void chooseFile(event.currentTarget.files?.[0])}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-white px-3 py-2 text-[11px] font-bold text-black hover:bg-white/90 disabled:opacity-50"
          >
            {busy ? (
              <LoaderCircle className="size-3.5 animate-spin" />
            ) : (
              <ImagePlus className="size-3.5" />
            )}
            {t(busy ? 'callView.uploadingBackground' : 'callView.uploadBackground')}
          </button>
          {error && (
            <p role="alert" className="mt-2 text-[10px] text-red-300">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
